import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import Footer from "./Footer";
import LoginModal from "../Auth/LoginModal";
import { getLocations, BRAND } from "../../config/locations";
import "../../styles/modern-landing.css";

// ---------------------------------------------------------------------------
// BrandHub — the "/" landing page. Brand-level content shared across all
// locations (ethos, coaching approach, app/login), plus "Choose your gym"
// cards generated from the location config.
//
// The gym cards use real react-router <Link> elements so the prerenderer
// (react-snap) can crawl them and emit static HTML for /sidcup and /ebbsfleet.
// ---------------------------------------------------------------------------
const BrandHub = () => {
	const [showLogin, setShowLogin] = useState(false);
	const { user, logout } = useAuth();
	const locations = getLocations();

	const handleLogout = async () => {
		try {
			await logout();
		} catch (error) {
			console.error("Logout error:", error);
		}
	};

	const pillars = [
		{
			title: "Coaching",
			description:
				"Every session is coach-led and built around you. Small groups of up to six mean individual attention with exercises tailored to your level, the energy of a group with the focus of personal training.",
		},
		{
			title: "Community",
			description:
				"Train alongside like-minded people working towards the same goal. A supportive environment that keeps you motivated and consistent, with events that bring people together beyond the workouts.",
		},
		{
			title: "Progress",
			description:
				"We help you understand your training, not just follow it. Proper technique, real accountability, and habits that stick, so you keep showing up and keep getting stronger.",
		},
	];

	return (
		<div className="min-vh-100">
			<Helmet>
				<title>
					Urban 59 - Small Group Personal Training | Choose Your Gym
				</title>
				<meta
					name="title"
					content="Urban 59 - Small Group Personal Training | Choose Your Gym"
				/>
				<meta
					name="description"
					content="Urban 59 small group personal training. Coach-led sessions of max 6 people, a supportive community, and real progress. Choose your location — Sidcup (open now) and Ebbsfleet (coming soon)."
				/>
				<link rel="canonical" href={`${BRAND.url}/`} />
				<meta property="og:type" content="website" />
				<meta property="og:url" content={`${BRAND.url}/`} />
				<meta
					property="og:title"
					content="Urban 59 - Small Group Personal Training | Choose Your Gym"
				/>
				<meta
					property="og:description"
					content="Coach-led small group personal training. Choose your Urban 59 location — Sidcup (open) and Ebbsfleet (coming soon)."
				/>
			</Helmet>

			{/* Navbar */}
			<nav
				className="modern-navbar"
				role="navigation"
				aria-label="Main navigation"
			>
				<div className="modern-navbar-container">
					<Link className="modern-navbar-brand" to="/">
						<img
							src={process.env.PUBLIC_URL + "/EU59_LogoAsset 7@4x.png"}
							alt="Urban 59"
							style={{ height: "60px", width: "auto" }}
						/>
					</Link>

					<div className="modern-navbar-menu">
						<a href="#locations" className="modern-nav-link">
							Locations
						</a>
						<a href="#ethos" className="modern-nav-link">
							Our Approach
						</a>
						<a href="#app" className="modern-nav-link">
							Member App
						</a>
					</div>

					<div className="modern-navbar-actions">
						{user ? (
							<>
								<span className="modern-user-text">Welcome, {user.email}</span>
								<button className="modern-btn-logout" onClick={handleLogout}>
									Logout
								</button>
							</>
						) : (
							<button
								className="modern-btn-trial"
								onClick={() => setShowLogin(true)}
							>
								LOGIN
							</button>
						)}
					</div>
				</div>
			</nav>

			{/* Hero */}
			<section className="modern-hero-section" aria-label="Hero section">
				<div className="modern-hero-container">
					<div className="modern-hero-content">
						<video
							autoPlay
							loop
							muted
							playsInline
							poster={process.env.PUBLIC_URL + "/WomanMattBW.jpg"}
							style={{
								position: "absolute",
								top: 0,
								left: 0,
								width: "100%",
								height: "100%",
								objectFit: "cover",
								zIndex: 0,
								opacity: 0.3,
								borderRadius: "inherit",
							}}
						>
							<source
								src={process.env.PUBLIC_URL + "/landscape promo websiteLOW.mp4"}
								type="video/mp4"
							/>
						</video>
						<div className="modern-hero-text">
							<h1 className="modern-hero-title" itemProp="headline">
								Build Strength.
								<br />
								Feel Confident.
								<br />
								Live Better.
							</h1>
							<p className="modern-hero-description">
								Urban59 combines expert coaching, purposeful training and a
								supportive community to help you build strength, confidence and
								lasting results. Choose your gym below.
							</p>
							<div className="modern-hero-actions">
								<button
									className="modern-btn-primary"
									onClick={() =>
										document
											.getElementById("locations")
											.scrollIntoView({ behavior: "smooth" })
									}
								>
									Choose Your Gym
									<svg
										width="20"
										height="20"
										viewBox="0 0 20 20"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M7.5 15L12.5 10L7.5 5"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
										/>
									</svg>
								</button>
							</div>
						</div>

						<div className="modern-hero-image">
							<img
								src={process.env.PUBLIC_URL + "/Sophie01.png"}
								alt="Urban 59 trainer"
								className="modern-hero-img"
							/>
						</div>
					</div>
				</div>
			</section>

			{/* Choose your gym */}
			<section
				id="locations"
				className="modern-about-section"
				aria-labelledby="locations-heading"
			>
				<div className="container">
					<div className="modern-about-header">
						<p className="section-label">Our Gyms</p>
						<h2 id="locations-heading" className="section-title">
							Choose Your <span className="highlight">Gym</span>
						</h2>
						<p className="modern-about-description">
							Pick your location to find out how to get started.
						</p>
					</div>

					<div className="row g-4 justify-content-center">
						{locations.map((loc) => {
							const comingSoon = loc.status === "coming-soon";
							return (
								<div key={loc.id} className="col-md-6 col-lg-5">
									<Link
										to={`/${loc.slug}`}
										className="location-card"
										style={{
											display: "block",
											textDecoration: "none",
											background: "#2d1b12",
											color: "#ffffff",
											borderRadius: "16px",
											padding: "2rem",
											height: "100%",
											border: "1px solid rgba(255,255,255,0.08)",
										}}
									>
										<div
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
												marginBottom: "0.75rem",
											}}
										>
											<h3
												style={{
													margin: 0,
													fontWeight: 800,
													textTransform: "uppercase",
												}}
											>
												{loc.shortName}
											</h3>
											<span
												style={{
													fontSize: "0.75rem",
													fontWeight: 700,
													textTransform: "uppercase",
													letterSpacing: "0.5px",
													padding: "0.25rem 0.6rem",
													borderRadius: "999px",
													background: comingSoon
														? "rgba(255,255,255,0.15)"
														: "#e85d3f",
													color: "#ffffff",
												}}
											>
												{comingSoon ? "Coming Soon" : "Open Now"}
											</span>
										</div>
										<p
											style={{
												color: "rgba(255,255,255,0.85)",
												marginBottom: "1.25rem",
												lineHeight: 1.6,
											}}
										>
											{loc.tagline}
										</p>
										<span
											className="modern-btn-primary"
											style={{
												display: "inline-flex",
												alignItems: "center",
												gap: "0.4rem",
											}}
										>
											{`Explore ${loc.shortName}`}
											<svg
												width="18"
												height="18"
												viewBox="0 0 20 20"
												fill="none"
												xmlns="http://www.w3.org/2000/svg"
											>
												<path
													d="M7.5 15L12.5 10L7.5 5"
													stroke="currentColor"
													strokeWidth="2"
													strokeLinecap="round"
													strokeLinejoin="round"
												/>
											</svg>
										</span>
									</Link>
								</div>
							);
						})}
					</div>
				</div>
			</section>

			{/* Ethos / coaching approach */}
			<section
				id="ethos"
				className="features-section"
				aria-labelledby="ethos-heading"
			>
				<div className="features-section-wrapper">
					<div className="container">
						<div className="row text-center mb-5">
							<div className="col-lg-8 mx-auto">
								<p className="section-label">Our approach</p>
								<h2 id="ethos-heading" className="section-title">
									The Urban 59 <span className="highlight">Way</span>
								</h2>
								<p className="lead text-muted">
									The same coaching philosophy across every Urban 59 gym.
								</p>
							</div>
						</div>

						<div className="row g-4">
							{pillars.map((pillar, index) => (
								<div key={index} className="col-md-4">
									<div style={{ textAlign: "center", padding: "1rem" }}>
										<div
											style={{
												width: "80px",
												height: "80px",
												borderRadius: "50%",
												margin: "0 auto 1.5rem",
												border: "3px solid #ffffff",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												backgroundColor: "rgba(255, 255, 255, 0.15)",
											}}
										>
											<span
												style={{
													fontSize: "2rem",
													fontWeight: 800,
													color: "#ffffff",
												}}
											>
												{index + 1}
											</span>
										</div>
										<h4
											style={{
												color: "#ffffff",
												fontWeight: 800,
												textTransform: "uppercase",
												marginBottom: "0.75rem",
											}}
										>
											{pillar.title}
										</h4>
										<p
											style={{
												color: "#ffffff",
												fontSize: "0.95rem",
												lineHeight: 1.6,
											}}
										>
											{pillar.description}
										</p>
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			</section>

			{/* Member app / login */}
			<section
				id="app"
				className="modern-about-section"
				aria-labelledby="app-heading"
			>
				<div className="container">
					<div className="modern-about-header">
						<p className="section-label">Members</p>
						<h2 id="app-heading" className="section-title">
							The Urban 59 <span className="highlight">App</span>
						</h2>
						<p className="modern-about-description">
							Already a member? Log in to track your workouts, log personal
							bests, and follow your progress across your sessions.
						</p>
						{!user && (
							<div
								style={{
									display: "flex",
									justifyContent: "center",
									marginTop: "1.5rem",
								}}
							>
								<button
									className="modern-btn-primary"
									onClick={() => setShowLogin(true)}
								>
									Member Login
								</button>
							</div>
						)}
					</div>
				</div>
			</section>

			<Footer />

			<LoginModal show={showLogin} onClose={() => setShowLogin(false)} />
		</div>
	);
};

export default BrandHub;
